import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  useTheme,
  Chip,
  Card,
  CardContent,
  Avatar,
  Container,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  PlayArrow,
  Stop,
  Navigation,
  Phone,
  Cancel,
  WifiOff,
  Timer,
  LocationOn,
  Person,
  LocalPhone,
  DirectionsCar,
  Build,
  Warning,
  Info,
  Mic,
  Send,
  SystemUpdateAlt,
  Description
} from '@mui/icons-material';
import { format } from 'date-fns';
import { startArrivalWatch, stopGeoWatch, haversineDistance, captureLocationNow } from '../../utils/geoUtils';
import api from '../../api/axios';
import { offlineManager } from '../../api/OfflineManager';
import JobCard from '../shared/JobCard';
import { useNetwork } from '../../context/NetworkContext';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from '../../context/LocationContext';
import LoadingState from '../shared/LoadingState';
import { parseUtc } from '../../utils/time';


// Mic button for note fields — fills the text, never auto-submits
// (the manager reads these; a bad transcript needs a worker's glance).
const DictateButton = ({ onText }) => {
  const recRef = useRef(null);
  const [rec, setRec] = useState(false);
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;

  const toggle = () => {
    if (rec && recRef.current) {
      recRef.current.stop();
      setRec(false);
      return;
    }
    const r = new SR();
    recRef.current = r;
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (e) => {
      const text = Array.from(e.results).map(x => x[0].transcript).join(' ');
      onText(text);
    };
    r.onerror = () => setRec(false);
    r.onend = () => setRec(false);
    r.start();
    setRec(true);
  };

  return (
    <IconButton onClick={toggle} size="small" edge="end"
      title={rec ? 'Stop dictation' : 'Dictate note'}
      sx={{ color: rec ? 'error.main' : 'text.secondary' }}>
      <Mic fontSize="small" />
    </IconButton>
  );
};

// Per-job client message thread — worker sees exactly what the client was
// told (confirmations, En Route ETA, replies) and can text back in-app via
// the business number. Composer respects the org's worker_client_messaging
// flag (off = read-only).
const JobMessageSection = ({ jobId }) => {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const load = async () => {
    try {
      const r = await api.get(`/worker/jobs/${jobId}/messages`);
      setThread(r.data);
    } catch (e) {
      setThread({ messages: [], messaging_mode: 'off' });
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/worker/jobs/${jobId}/messages`);
        setThread(r.data);
      } catch (e) {
        setThread({ messages: [], messaging_mode: 'off' });
      }
    })();
  }, [jobId]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true); setSendError('');
    try {
      await api.post(`/worker/jobs/${jobId}/message`, { text: text.trim() });
      setText('');
      await load();
    } catch (e) {
      setSendError(e.response?.data?.detail || 'Send failed');
    }
    setSending(false);
  };

  if (!thread) return null;
  const msgs = thread.messages || [];
  const canSend = thread.messaging_mode !== 'off';

  return (
    <Box sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        CLIENT MESSAGES
      </Typography>
      <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {msgs.length === 0 && (
          <Typography variant="caption" color="text.secondary">No messages yet for this job.</Typography>
        )}
        {msgs.map((m, i) => (
          <Box key={i} sx={{
            alignSelf: m.direction === 'inbound' ? 'flex-start' : 'flex-end',
            maxWidth: '85%', bgcolor: m.direction === 'inbound' ? 'grey.100' : '#E0F2FE',
            borderRadius: 2, px: 1.2, py: 0.8,
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {m.direction === 'inbound' ? '📩 client' : `📤 ${m.from || 'system'}`}
              {m.status && m.status !== 'sent' ? ` · ${m.status}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
              {m.ts ? parseUtc(m.ts).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
            </Typography>
          </Box>
        ))}
      </Box>
      {canSend && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            fullWidth size="small" placeholder="Message the client…"
            value={text} onChange={(e) => setText(e.target.value)}
            disabled={sending}
            InputProps={{ endAdornment: <DictateButton onText={(t) => setText(p => p ? p + ' ' + t : t)} /> }}
          />
          <IconButton color="primary" onClick={send} disabled={sending || !text.trim()}>
            <Send />
          </IconButton>
        </Box>
      )}
      {canSend && thread.messaging_mode === 'draft' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Messages are sent to the client after manager approval.
        </Typography>
      )}
      {sendError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{sendError}</Typography>
      )}
    </Box>
  );
};

const WorkerDashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { isOnline } = useNetwork();
  const { trackingActive, lastLocation, error: locationError } = useLocation();
  
  const employeeId = user?.employee_id;
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockTime, setClockTime] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [failDialog, setFailDialog] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [failNotes, setFailNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [nudges, setNudges] = useState([]);
  const [helpDialog, setHelpDialog] = useState(false);
  const [helpReason, setHelpReason] = useState('');
  const [helpDetails, setHelpNotes] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [jobDetailDialog, setJobDetailDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');
  const [arrivalSuggestion, setArrivalSuggestion] = useState(null);
  const geoWatchRef = useRef(null);
  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [pendingOutcomes, setPendingOutcomes] = useState([]);
  const [outcomeMode, setOutcomeMode] = useState(null); // 'completed' | 'failed' | 'rescheduled'
  const [outcomeReason, setOutcomeReason] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [outcomeError, setOutcomeError] = useState('');

  useEffect(() => {
    loadTodayJobs();
    loadPendingOutcomes();
    if (employeeId) {
        loadClockStatus();
        loadActiveNudges();
        const interval = setInterval(() => {
            loadActiveNudges();
            loadPendingOutcomes();
        }, 60000); // Check for nudges and overdue jobs every minute
        return () => clearInterval(interval);
    }
  }, [isOnline, employeeId]); // Reload when coming back online or employeeId changes

  const loadPendingOutcomes = async () => {
    try {
        const response = await api.get('/worker/pending-outcomes');
        // Jobs whose outcome is queued offline aren't actually pending —
        // the override carries it until the server confirms.
        const overrides = await offlineManager.getOutcomeOverrides();
        const rows = (response.data || []).filter(j => {
          const ov = overrides[j.id];
          return !(ov && ['Completed', 'Failed', 'Rescheduled', 'Cancelled'].includes(ov.status));
        });
        setPendingOutcomes(rows);
    } catch (err) {
        console.error('Failed to load pending outcomes:', err);
    }
  };

  const resetOutcomeForm = () => {
    setOutcomeMode(null);
    setOutcomeReason('');
    setOutcomeNotes('');
    setOutcomeError('');
  };

  const handleSkipForNow = async () => {
    // 'Skip for now' — declared park, not an outcome. Job stays on the run;
    // the gate stops blocking; EOD accountability stays server-side.
    const job = pendingOutcomes[0];
    if (!job) return;
    try {
      await api.post(`/worker/jobs/${job.id}/defer`);
      setPendingOutcomes(prev => prev.filter(j => j.id !== job.id));
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, deferred: true } : j));
      resetOutcomeForm();
    } catch (err) {
      setOutcomeError("Couldn't skip — try again or record an outcome.");
    }
  };

  const handleResolveOutcome = async () => {
    const job = pendingOutcomes[0];
    if (!job || !outcomeMode) return;

    const newStatus = outcomeMode === 'completed' ? 'Completed'
      : outcomeMode === 'failed' ? 'Failed' : 'Rescheduled';
    const extras = outcomeMode === 'completed'
      ? { worker_notes: outcomeNotes || undefined }
      : { failure_reason: outcomeReason, failure_notes: outcomeNotes || undefined };

    const ok = await updateJobStatus(job.id, newStatus, extras);
    if (ok) {
      setPendingOutcomes(prev => prev.filter(j => j.id !== job.id));
      resetOutcomeForm();
    } else {
      setOutcomeError('Could not save the outcome. Check your connection and try again.');
    }
  };

  const loadActiveNudges = async () => {
    try {
        // We'll need a new endpoint or use AIState query
        const response = await api.get('/worker/nudges'); 
        setNudges(response.data || []);
    } catch (err) {
        console.error('Failed to load nudges:', err);
    }
  };

  const loadClockStatus = async () => {
    try {
        const response = await api.get(`/worker/clock/status?employee_id=${employeeId}`);
        setClockedIn(response.data.clocked_in);
        if (response.data.last_event) {
            setClockTime(parseUtc(response.data.last_event.timestamp));
        }
    } catch (err) {
        console.error('Failed to load clock status:', err);
    }
  };

  const loadTodayJobs = async () => {
    setLoading(true);
    try {
      // Use the specific my-jobs endpoint for workers
      const response = await api.get('/admin/my-jobs'); 
      const loadedJobs = response.data || [];
      // Offline outcome overrides — jobs marked done while offline keep
      // their outcome until the server confirms the queued write landed.
      try {
        const overrides = await offlineManager.getOutcomeOverrides();
        for (const j of loadedJobs) {
          const ov = overrides[j.id];
          if (!ov) continue;
          if (j.status === ov.status) {
            // Server already shows it — the sync landed; drop the override.
            await offlineManager.clearOutcomeOverride(j.id);
          } else {
            j.status = ov.status;
            Object.assign(j, ov.extras || {});
          }
        }
      } catch (oe) {
        console.warn('outcome override merge failed:', oe);
      }
      setJobs(loadedJobs);
      const enRouteJob = loadedJobs.find(j => j.status === 'En Route' && j.latitude && j.longitude);
      if (enRouteJob) {
        startArrivalDetection(enRouteJob);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
    setLoading(false);
  };

  const handleClockIn = async () => {
    if (!employeeId) return;
    captureLocationNow();
    setActionLoading(true);
    try {
      await api.post('/worker/clock', {
        employee_id: employeeId,
        event_type: 'clock_in',
        location_lat: lastLocation?.lat?.toString(),
        location_lng: lastLocation?.lng?.toString()
      });
      setClockedIn(true);
      setClockTime(new Date());
    } catch (err) {
      console.error('Clock in failed:', err);
    }
    setActionLoading(false);
  };

  const handleClockOut = async () => {
    if (!employeeId) return;
    setActionLoading(true);
    try {
      await api.post('/worker/clock', {
        employee_id: employeeId,
        event_type: 'clock_out',
        location_lat: lastLocation?.lat?.toString(),
        location_lng: lastLocation?.lng?.toString()
      });
      setClockedIn(false);
      setClockTime(null);
    } catch (err) {
      console.error('Clock out failed:', err);
    }
    setActionLoading(false);
  };

  const updateJobStatus = async (jobId, newStatus, extras = {}) => {
    captureLocationNow();  // every status move = a location fix
    setActionLoading(true);
    try {
      // Optimistic update first
      setJobs(jobs.map(j => j.id === jobId ? { ...j, status: newStatus, ...extras } : j));
      setSelectedJob(prev => prev ? { ...prev, status: newStatus, ...extras } : prev);

      // Last-known fix rides the status write — the tap is the most
      // reliable ping before a nav intent backgrounds the PWA.
      const payload = { status: newStatus, ...extras };
      if (lastLocation?.lat && lastLocation?.lng) {
        payload.location_lat = lastLocation.lat;
        payload.location_lng = lastLocation.lng;
      }
      // API call (will be queued if offline)
      const resp = await api.put(`/admin/jobs/${jobId}/status`, payload);
      // Queued-offline write — persist the outcome locally so a refetch
      // doesn't wipe it before the sync replay lands.
      if (resp?.isQueued) {
        await offlineManager.setOutcomeOverride(jobId, newStatus, extras);
      }
      setActionLoading(false);
      return true;
    } catch (err) {
      // Sequential gate: the API refused because an earlier job still has no
      // outcome — force that job's outcome modal open immediately.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'needs_prior_outcome') {
        setPendingOutcomes(prev => {
          const prior = {
            id: detail.job_id,
            location: detail.location,
            status: detail.status,
            client_name: detail.client_name || null,
            description: detail.message,
            scheduled_date: '',
            scheduled_time: null,
          };
          return prev.some(j => j.id === prior.id) ? prev : [prior, ...prev];
        });
        setJobDetailDialog(false);
        loadTodayJobs();
        setActionLoading(false);
        return false;
      }
      console.error('Status update failed:', err);
      // Revert optimistic update if actual failure (not just offline queue)
      // Note: Offline queue returns success-like response, so this catch only hits on real errors
      loadTodayJobs();
    }
    setActionLoading(false);
    return false;
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setJobDetailDialog(true);
    // Pull the full detail — list rows don't carry files/messages/audit.
    api.get(`/admin/jobs/${job.id}`)
      .then(r => setSelectedJob(prev => (prev && prev.id === job.id ? { ...prev, ...r.data } : prev)))
      .catch(() => {});
  };

  const startArrivalDetection = (job) => {
    if (!job || !job.latitude || !job.longitude) return;
    stopArrivalDetection();
    geoWatchRef.current = startArrivalWatch(
      job.latitude,
      job.longitude,
      (dist) => {
        setArrivalSuggestion({ jobId: job.id, distance: Math.round(dist) });
      },
      null,
      (err) => {
        console.warn('Geolocation error:', err.message);
      }
    );
  };

  const stopArrivalDetection = () => {
    if (geoWatchRef.current !== null) {
      stopGeoWatch(geoWatchRef.current);
      geoWatchRef.current = null;
    }
    setArrivalSuggestion(null);
  };

  useEffect(() => {
    return () => stopArrivalDetection();
  }, []);

  // Sequential close-off gate — you can't navigate to / start the next
  // job while an earlier one is still un-outcome'd.
  // 'Waiting on Client' MUST be here — a job with a confirmation out and
  // no reply yet is still a job the worker has to run. Excluding it hides
  // the card (no En Route, no arrival detection, no completion flow).
  const ACTIVE_JOB_STATUSES = ['Scheduled', 'Tentative', 'Locked', 'Confirmed', 'Waiting on Client', 'En Route', 'On Site'];
  const _sorted = [...jobs].sort((a, b) => {
    const da = a.scheduled_date || '', db = b.scheduled_date || '';
    if (da !== db) return da < db ? -1 : 1;
    const ta = a.scheduled_time || '', tb = b.scheduled_time || '';
    return ta < tb ? -1 : ta > tb ? 1 : 0;
  });
  // Deferred ('skipped for now') jobs park — they don't gate the next stop
  // and they don't count as "next up" until the worker picks them back up.
  const firstOpenJob = _sorted.find(j =>
    ACTIVE_JOB_STATUSES.includes(j.status) && !j.deferred);
  const blockedByPrior = !!(selectedJob && firstOpenJob
    && selectedJob.id !== firstOpenJob.id
    && ACTIVE_JOB_STATUSES.includes(selectedJob.status)
    && !selectedJob.deferred);

  const handleStartJob = async () => {
    if (!selectedJob) return;
    // Ping BEFORE the nav intent — window.open to Maps backgrounds the PWA
    // immediately, and any request still pending dies with it. keepalive
    // fetches are guaranteed to complete after the page suspends.
    try {
      const token = localStorage.getItem('token');
      if (token && lastLocation?.lat && lastLocation?.lng) {
        fetch('/worker/location', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ latitude: lastLocation.lat, longitude: lastLocation.lng }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) { /* ping best-effort */ }
    // Open nav FIRST — window.open inside the click gesture survives popup
    // blockers; after an await it doesn't.
    if (selectedJob.location) {
      const query = encodeURIComponent(selectedJob.location);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${query}`, '_blank');
    }
    await updateJobStatus(selectedJob.id, 'En Route');
    setJobDetailDialog(false);
    startArrivalDetection(selectedJob);
  };

  const handleOnSite = async () => {
    if (!selectedJob) return;
    await updateJobStatus(selectedJob.id, 'On Site');
    setJobDetailDialog(false);
    stopArrivalDetection();
  };

  const handleArrivalConfirm = async () => {
    if (!arrivalSuggestion) return;
    await updateJobStatus(arrivalSuggestion.jobId, 'On Site');
    stopArrivalDetection();
  };

  const handleCompleteJob = async () => {
    if (!selectedJob) return;
    await updateJobStatus(selectedJob.id, 'Completed', {
      worker_notes: completeNotes || undefined
    });
    setCompleteDialog(false);
    setCompleteNotes('');
    setJobDetailDialog(false);
    stopArrivalDetection();
  };

  const handleFailJob = () => {
    setFailDialog(true);
  };

  const handleFail = () => {
    if (selectedJob) {
      updateJobStatus(selectedJob.id, 'Failed', {
        failure_reason: failReason,
        failure_notes: failNotes
      });
      setFailDialog(false);
      setJobDetailDialog(false);
      setFailReason('');
      setFailNotes('');
      stopArrivalDetection();
    }
  };

  const handleReschedule = () => {
    if (selectedJob) {
      updateJobStatus(selectedJob.id, 'Rescheduled', {
        failure_reason: rescheduleReason,
        failure_notes: rescheduleNotes
      });
      setRescheduleDialog(false);
      setJobDetailDialog(false);
      setRescheduleReason('');
      setRescheduleNotes('');
      stopArrivalDetection();
    }
  };

  const handleEscalate = async () => {
    captureLocationNow();
    if (!employeeId) return;
    setEscalating(true);
    try {
      await api.post('/worker/escalate', {
        employee_id: employeeId,
        job_id: selectedJob?.id,
        reason: helpReason,
        details: helpDetails
      });
      setHelpDialog(false);
      setHelpReason('');
      setHelpNotes('');
      // Show success alert?
    } catch (err) {
      console.error('Escalation failed:', err);
    }
    setEscalating(false);
  };

  const handleDismissNudge = async (nudgeId) => {
    try {
        await api.post(`/worker/nudges/${nudgeId}/dismiss`);
        setNudges(nudges.filter(n => n.id !== nudgeId));
    } catch (err) {
        console.error('Failed to dismiss nudge:', err);
    }
  };

  if (loading) {
    return <LoadingState message="Loading your dashboard..." />;
  }

  // Local calendar date — toISOString() is UTC and blanks out today's jobs
  // in Brisbane mornings (local Mon <10am = UTC Sun).
  const todayStr = new Date().toLocaleDateString('en-CA');
  const jobDateStr = (j) => j.scheduled_date ? j.scheduled_date.split('T')[0] : null;
  // Run-sheet order = the manager's routed order (scheduled_time asc).
  // The API returns raw row order — sort so the list IS the route.
  const toMins = (t) => {
    if (!t) return 9999;
    const [h, m] = String(t).slice(0, 5).split(':').map(Number);
    return h * 60 + (m || 0);
  };
  const activeJobs = jobs
    .filter(j => jobDateStr(j) === todayStr && !['Completed', 'Failed', 'Cancelled'].includes(j.status))
    .sort((a, b) => toMins(a.scheduled_time) - toMins(b.scheduled_time));
  const completedToday = jobs.filter(j => jobDateStr(j) === todayStr && j.status === 'Completed');
  const nextUpId = (activeJobs.find(j => !j.deferred) || {}).id || null;

  return (
    <Box sx={{ pb: 10 }}>
        {/* Header */}
       <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">My Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Tooltip title="Get the latest app version">
          <IconButton onClick={async () => {
            // Hard-refresh the PWA: clear every cached asset + unregister
            // the service worker, then reload — pulls the newest build.
            try {
              if ('caches' in window) {
                for (const k of await caches.keys()) await caches.delete(k);
              }
              for (const r of (await navigator.serviceWorker?.getRegistrations()) || []) {
                await r.unregister();
              }
            } catch (_) { /* reload regardless */ }
            window.location.reload(true);
          }} size="small" sx={{ mt: 0.5 }}>
            <SystemUpdateAlt fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Offline Alert */}
      {!isOnline && (
        <Alert 
          severity="warning" 
          icon={<WifiOff fontSize="inherit" />} 
          variant="filled"
          sx={{ mb: 3, borderRadius: 2 }}
        >
          You are working offline. Actions will be queued and synced when you reconnect.
        </Alert>
      )}

      {/* AI Nudges */}
      {nudges.map((nudge) => (
        <Alert 
          key={nudge.id}
          severity="info" 
          variant="outlined"
          sx={{ mb: 3, borderRadius: 3, border: '1px solid #3B82F6', bgcolor: '#EFF6FF' }}
          action={
            <Button color="inherit" size="small" onClick={() => handleDismissNudge(nudge.id)}>
              DISMISS
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>{nudge.message}</Typography>
        </Alert>
      ))}

      {/* Clock In/Out Card */}
      <Card 
        elevation={0}
        sx={{ 
          mb: 4, 
          border: '1px solid',
          borderColor: clockedIn ? 'success.light' : 'error.light',
          bgcolor: clockedIn ? '#F0FDF4' : '#FEF2F2',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar 
                sx={{ 
                    width: 72, 
                    height: 72, 
                    bgcolor: clockedIn ? 'success.main' : 'error.main',
                    mb: 2,
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                }}
            >
                {clockedIn ? <PlayArrow sx={{ fontSize: 40 }} /> : <Stop sx={{ fontSize: 40 }} />}
            </Avatar>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                {clockedIn ? 'You are Clocked In' : 'You are Clocked Out'}
            </Typography>
            
            {clockedIn && clockTime ? (
                 <Chip 
                    icon={<Timer fontSize="small" />}
                    label={`Started at ${clockTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`} 
                    color="success" 
                    variant="outlined" 
                    sx={{ mb: 3, bgcolor: 'white', fontWeight: 600, border: '1px solid', borderColor: 'success.main' }}
                 />
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Ready to start your shift?
                </Typography>
            )}

            <Button
                variant="contained"
                color={clockedIn ? 'error' : 'success'}
                size="large"
                onClick={clockedIn ? handleClockOut : handleClockIn}
                disabled={actionLoading}
                sx={{ 
                    minWidth: 200, 
                    borderRadius: 3, 
                    fontWeight: 700,
                    py: 1.5,
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                    textTransform: 'none',
                    fontSize: '1rem'
                }}
            >
                {clockedIn ? 'End Shift' : 'Start Shift'}
            </Button>

            {clockedIn && (
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setHelpDialog(true)}
                    sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}
                >
                    NEED HELP / ESCALATE
                </Button>
            )}
        </CardContent>
      </Card>

      {/* Today's Jobs */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>
            Today's Schedule
        </Typography>
        <Chip 
            label={`${activeJobs.length} Active`} 
            size="small" 
            color="primary" 
            sx={{ fontWeight: 600, borderRadius: 1.5 }} 
        />
      </Box>

      {activeJobs.length === 0 ? (
        <Paper 
            elevation={0}
            sx={{ 
                p: 6, 
                textAlign: 'center', 
                borderRadius: 3, 
                bgcolor: '#F8FAFC',
                border: '1px dashed #E2E8F0'
            }}
        >
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>No jobs scheduled today</Typography>
          <Typography variant="body2" color="text.secondary">You're all caught up! Take a break or check back later.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {activeJobs.map((job) => (
            <Box key={job.id} sx={job.deferred ? { opacity: 0.55 } : undefined}>
              {job.id === nextUpId && (
                <Chip label="NEXT UP" size="small" color="primary"
                  sx={{ mb: 0.5, fontWeight: 800, fontSize: 11, borderRadius: 1.5 }} />
              )}
              {job.deferred && (
                <Chip label="SKIPPED — COMING BACK" size="small" color="warning"
                  sx={{ mb: 0.5, fontWeight: 800, fontSize: 11, borderRadius: 1.5 }} />
              )}
              <JobCard
                job={job}
                onClick={handleJobClick}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Completed Today */}
      {completedToday.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'text.secondary' }}>
            Completed Today
          </Typography>
          {completedToday.map((job) => (
            <Paper 
                key={job.id} 
                elevation={0}
                sx={{ 
                    p: 2, 
                    mb: 1.5, 
                    opacity: 0.8, 
                    bgcolor: '#F1F5F9',
                    border: '1px solid #E2E8F0',
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{job.location}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {job.client_name} • {job.job_type || 'Standard'}
                    </Typography>
                </Box>
                <CheckCircle color="success" fontSize="small" />
            </Paper>
          ))}
        </Box>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={jobDetailDialog} onClose={() => setJobDetailDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Job Details
          {selectedJob && (
            <Chip
              label={selectedJob.status}
              size="small"
              sx={{
                ml: 1,
                fontWeight: 600,
                bgcolor: selectedJob.status === 'En Route' ? '#E0F7FA'
                  : selectedJob.status === 'On Site' ? '#F0FDF4'
                  : selectedJob.status === 'Completed' ? '#F5F3FF'
                  : selectedJob.status === 'Failed' ? '#FEF2F2'
                  : '#F1F5F9',
                color: selectedJob.status === 'En Route' ? '#06B6D4'
                  : selectedJob.status === 'On Site' ? '#22C55E'
                  : selectedJob.status === 'Completed' ? '#8B5CF6'
                  : selectedJob.status === 'Failed' ? '#EF4444'
                  : '#64748B',
              }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Address */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationOn color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>{selectedJob.location}</Typography>
                  {selectedJob.client_name && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedJob.client_name}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Job Description */}
              {selectedJob.description && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Build color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Job Description</Typography>
                    <Typography variant="body2">{selectedJob.description}</Typography>
                  </Box>
                </Box>
              )}

              {/* Client Phone — tap to call */}
              {selectedJob.client_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalPhone color="action" />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`tel:${selectedJob.client_phone}`}
                    sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
                  >
                    Call Client: {selectedJob.client_phone}
                  </Typography>
                </Box>
              )}

              {/* Client message thread + composer */}
              <JobMessageSection jobId={selectedJob.id} />

              {/* Time */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timer color="action" />
                <Typography variant="body2">
                  {selectedJob.scheduled_time
                    ? format(new Date(`1970-01-01T${selectedJob.scheduled_time}`), 'h:mm a')
                    : selectedJob.scheduled_date
                      ? format(new Date(selectedJob.scheduled_date), 'EEE d MMM')
                      : 'TBD'}
                </Typography>
              </Box>

              {/* Site Access */}
              {selectedJob.site_access && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Info color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Site Access</Typography>
                    <Typography variant="body2">{selectedJob.site_access}</Typography>
                  </Box>
                </Box>
              )}

              {/* Site Contact */}
              {selectedJob.site_contact_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="action" />
                  <Typography variant="body2">
                    {selectedJob.site_contact_name}
                    {selectedJob.site_contact_phone && ` • ${selectedJob.site_contact_phone}`}
                  </Typography>
                </Box>
              )}

              {/* Special Instructions */}
              {selectedJob.special_instructions && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Warning color="warning" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Special Instructions</Typography>
                    <Typography variant="body2">{selectedJob.special_instructions}</Typography>
                  </Box>
                </Box>
              )}

              {/* Attachments / Downloads — booking-form plans & docs.
                  JobFile rows first; then any bare URL in the instructions
                  (legacy imports kept plans as 'Plans: <url>' text). */}
              {(() => {
                const files = selectedJob.files || [];
                const urlRe = /https?:\/\/[^\s)]+/g;
                const textLinks = (selectedJob.special_instructions || '')
                  .match(urlRe) || [];
                const known = new Set(files.map(f => f.url));
                const legacyLinks = textLinks.filter(u => !known.has(u));
                if (files.length === 0 && legacyLinks.length === 0) return null;
                return (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Attachments
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {files.map(f => (
                        <Button key={f.id} variant="outlined" size="small" fullWidth
                          startIcon={<Description />}
                          onClick={() => window.open(f.url, '_blank')}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2 }}>
                          {f.file_name || 'Attachment'}
                        </Button>
                      ))}
                      {legacyLinks.map(u => (
                        <Button key={u} variant="outlined" size="small" fullWidth
                          startIcon={<Description />}
                          onClick={() => window.open(u, '_blank')}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2 }}>
                          {u.length > 48 ? u.slice(0, 48) + '…' : u}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {/* Worker Notes (if any) */}
              {selectedJob.worker_notes && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Build color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Your Notes</Typography>
                    <Typography variant="body2">{selectedJob.worker_notes}</Typography>
                  </Box>
                </Box>
              )}

              {/* Failure info (if failed) */}
              {selectedJob.failure_reason && (
                <Alert severity="error" variant="outlined">
                  <Typography variant="caption" fontWeight={600}>Failed: {selectedJob.failure_reason}</Typography>
                  {selectedJob.failure_notes && (
                    <Typography variant="body2">{selectedJob.failure_notes}</Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, flexDirection: 'column', gap: 1 }}>
          {/* Context-aware action buttons */}
          {selectedJob && !['Completed', 'Failed', 'Cancelled'].includes(selectedJob.status) && (
            <>
              {blockedByPrior && (
                <Alert severity="warning" sx={{ width: '100%', mb: 1 }}
                  action={
                    <Button size="small" variant="outlined" color="warning"
                      onClick={() => setSelectedJob(firstOpenJob)}>
                      Go to #{firstOpenJob.id}
                    </Button>
                  }>
                  Close off job #{firstOpenJob.id}
                  {firstOpenJob.location ? ` (${firstOpenJob.location.split(',')[0]})` : ''} first.
                </Alert>
              )}
              {['Locked', 'Confirmed', 'Scheduled', 'Tentative', 'Waiting on Client'].includes(selectedJob.status) && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<DirectionsCar />}
                  onClick={handleStartJob}
                  disabled={actionLoading || blockedByPrior}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                >
                  En Route
                </Button>
              )}
              {selectedJob.status === 'En Route' && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<LocationOn />}
                  onClick={handleOnSite}
                  disabled={actionLoading}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                >
                  Arrived On Site
                </Button>
              )}
              {selectedJob.status === 'On Site' && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<CheckCircle />}
                  onClick={() => setCompleteDialog(true)}
                  disabled={actionLoading}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                >
                  Complete Job
                </Button>
              )}
              {/* Navigate button - opens Google Maps */}
              {selectedJob.location && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<Navigation />}
                  disabled={blockedByPrior}
                  onClick={() => {
                    const query = encodeURIComponent(selectedJob.location);
                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${query}`, '_blank');
                  }}
                  sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
                >
                  Navigate to Site
                </Button>
              )}
              {/* Reschedule button - for jobs not yet started or en route */}
              {['Locked', 'Confirmed', 'En Route'].includes(selectedJob.status) && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  size="large"
                  startIcon={<Warning />}
                  onClick={() => setRescheduleDialog(true)}
                  disabled={actionLoading}
                  sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
                >
                  Request Reschedule
                </Button>
              )}
              {/* Fail button - always available for active jobs */}
              <Button
                fullWidth
                variant="outlined"
                color="error"
                size="large"
                startIcon={<Cancel />}
                onClick={handleFailJob}
                disabled={actionLoading}
                sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
              >
                Mark as Failed
              </Button>
            </>
          )}
          <Button
            fullWidth
            onClick={() => setJobDetailDialog(false)}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog open={completeDialog} onClose={() => setCompleteDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Complete Job</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mark this job as completed. Add any notes about the work done.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Completion Notes (optional)"
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
            placeholder="What was done, any issues, materials used..."
            variant="outlined"
            InputProps={{ endAdornment: <DictateButton onText={(t) => setCompleteNotes(p => p ? p + ' ' + t : t)} /> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCompleteDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleCompleteJob}
            color="success"
            variant="contained"
            disabled={actionLoading}
            sx={{ fontWeight: 600 }}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fail Dialog */}
      <Dialog open={failDialog} onClose={() => setFailDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Mark Job as Failed</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Reason"
            value={failReason}
            onChange={(e) => setFailReason(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            SelectProps={{ native: true }}
            variant="outlined"
          >
            <option value="">Select reason...</option>
            <option value="No Access">No Access</option>
            <option value="Client Not Home">Client Not Home</option>
            <option value="Weather">Weather</option>
            <option value="Equipment Issue">Equipment Issue</option>
            <option value="Safety Concern">Safety Concern</option>
            <option value="Other">Other</option>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={failNotes}
            onChange={(e) => setFailNotes(e.target.value)}
            placeholder="Additional details..."
            variant="outlined"
            InputProps={{ endAdornment: <DictateButton onText={(t) => setFailNotes(p => p ? p + ' ' + t : t)} /> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setFailDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleFail}
            color="error"
            variant="contained"
            disabled={!failReason}
            sx={{ fontWeight: 600 }}
          >
            Mark Failed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialog} onClose={() => setRescheduleDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Request Reschedule</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This job will be sent back to the manager for rescheduling. Let them know why.
          </Typography>
          <TextField
            select
            fullWidth
            label="Reason"
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            sx={{ mb: 2 }}
            SelectProps={{ native: true }}
            variant="outlined"
          >
            <option value="">Select reason...</option>
            <option value="Equipment Unavailable">Equipment Unavailable</option>
            <option value="Weather">Weather</option>
            <option value="Access Issue">Access Issue</option>
            <option value="Time Constraint">Time Constraint</option>
            <option value="Client Request">Client Request</option>
            <option value="Other">Other</option>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={rescheduleNotes}
            onChange={(e) => setRescheduleNotes(e.target.value)}
            placeholder="Additional details for the manager..."
            variant="outlined"
            InputProps={{ endAdornment: <DictateButton onText={(t) => setRescheduleNotes(p => p ? p + ' ' + t : t)} /> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRescheduleDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleReschedule}
            color="warning"
            variant="contained"
            disabled={!rescheduleReason}
            sx={{ fontWeight: 600 }}
          >
            Request Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help / Escalate Dialog */}
      <Dialog open={helpDialog} onClose={() => setHelpDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Request Urgent Help</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will immediately notify managers that you need assistance.
          </Typography>
          <TextField
            select
            fullWidth
            label="Reason"
            value={helpReason}
            onChange={(e) => setHelpReason(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            SelectProps={{ native: true }}
            variant="outlined"
          >
            <option value="">Select reason...</option>
            <option value="Equipment Failure">Equipment Failure</option>
            <option value="Safety Issue">Safety Issue</option>
            <option value="Site Access Problem">Site Access Problem</option>
            <option value="Health Emergency">Health Emergency</option>
            <option value="Other">Other</option>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Details"
            value={helpDetails}
            onChange={(e) => setHelpNotes(e.target.value)}
            placeholder="Describe what's happening..."
            variant="outlined"
            InputProps={{ endAdornment: <DictateButton onText={(t) => setHelpNotes(p => p ? p + ' ' + t : t)} /> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setHelpDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleEscalate}
            color="warning"
            variant="contained"
            disabled={!helpReason || escalating}
            sx={{ fontWeight: 600 }}
          >
            {escalating ? <CircularProgress size={24} /> : 'Notify Managers'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GPS Arrival Suggestion */}
      <Snackbar
        open={!!arrivalSuggestion}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={null}
        sx={{ top: { xs: 80, sm: 90 } }}
      >
        <Alert
          severity="info"
          icon={<LocationOn />}
          variant="filled"
          sx={{
            alignItems: 'center',
            borderRadius: 2,
            boxShadow: 3,
            '& .MuiAlert-action': { flexWrap: 'nowrap' }
          }}
          action={
            <>
              <Button
                color="success"
                variant="contained"
                size="small"
                onClick={handleArrivalConfirm}
                disabled={actionLoading}
                sx={{ fontWeight: 700, textTransform: 'none', mr: 1 }}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'On Site'}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => setArrivalSuggestion(null)}
                sx={{ fontWeight: 600, textTransform: 'none' }}
              >
                Dismiss
              </Button>
            </>
          }
        >
          You're near the job site{arrivalSuggestion?.distance ? ` (${arrivalSuggestion.distance}m)` : ''}. Mark as On Site?
        </Alert>
      </Snackbar>

      {/* Pending Outcomes — blocking dialog, cannot be dismissed until every
          overdue job has a recorded outcome */}
      <Dialog
        open={pendingOutcomes.length > 0}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Outcome Required
          {pendingOutcomes.length > 1 && (
            <Chip label={`1 of ${pendingOutcomes.length}`} size="small" sx={{ ml: 1, fontWeight: 600 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {pendingOutcomes[0] && (
            <Box>
              <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                This job's scheduled time has passed. Record what happened — jobs can't be skipped without an outcome.
              </Alert>

              <Typography variant="subtitle1" fontWeight={700}>
                {pendingOutcomes[0].location}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pendingOutcomes[0].client_name ? `${pendingOutcomes[0].client_name} • ` : ''}
                {pendingOutcomes[0].description}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scheduled {pendingOutcomes[0].scheduled_date}
                {pendingOutcomes[0].scheduled_time ? ` at ${pendingOutcomes[0].scheduled_time}` : ''}
                {' '}— last status: {pendingOutcomes[0].status}
              </Typography>

              {outcomeMode === 'completed' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Completion Notes (optional)"
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  placeholder="What was done, any issues, materials used..."
                  variant="outlined"
                  InputProps={{ endAdornment: <DictateButton onText={(t) => setOutcomeNotes(p => p ? p + ' ' + t : t)} /> }}
                />
              )}

              {(outcomeMode === 'failed' || outcomeMode === 'rescheduled') && (
                <>
                  <TextField
                    select
                    fullWidth
                    label="Reason"
                    value={outcomeReason}
                    onChange={(e) => setOutcomeReason(e.target.value)}
                    sx={{ mb: 2 }}
                    SelectProps={{ native: true }}
                    variant="outlined"
                  >
                    <option value="">Select reason...</option>
                    {outcomeMode === 'failed' ? (
                      <>
                        <option value="No Access">No Access</option>
                        <option value="Client Not Home">Client Not Home</option>
                        <option value="Weather">Weather</option>
                        <option value="Equipment Issue">Equipment Issue</option>
                        <option value="Safety Concern">Safety Concern</option>
                        <option value="Other">Other</option>
                      </>
                    ) : (
                      <>
                        <option value="Equipment Unavailable">Equipment Unavailable</option>
                        <option value="Weather">Weather</option>
                        <option value="Access Issue">Access Issue</option>
                        <option value="Time Constraint">Time Constraint</option>
                        <option value="Client Request">Client Request</option>
                        <option value="Other">Other</option>
                      </>
                    )}
                  </TextField>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    value={outcomeNotes}
                    onChange={(e) => setOutcomeNotes(e.target.value)}
                    placeholder={outcomeMode === 'failed' ? 'Additional details...' : 'Additional details for the manager...'}
                    variant="outlined"
                    InputProps={{ endAdornment: <DictateButton onText={(t) => setOutcomeNotes(p => p ? p + ' ' + t : t)} /> }}
                  />
                </>
              )}

              {outcomeError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{outcomeError}</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, flexDirection: 'column', gap: 1 }}>
          {!outcomeMode ? (
            <>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => setOutcomeMode('completed')}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
              >
                Job Completed
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                startIcon={<Cancel />}
                onClick={() => setOutcomeMode('failed')}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
              >
                Job Failed
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                size="large"
                startIcon={<Warning />}
                onClick={() => setOutcomeMode('rescheduled')}
                sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
              >
                Needs Reschedule
              </Button>
              <Button
                fullWidth
                variant="text"
                size="large"
                onClick={handleSkipForNow}
                sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
              >
                Skip for now — I'll come back to it
              </Button>
            </>
          ) : (
            <>
              <Button
                fullWidth
                variant="contained"
                color={outcomeMode === 'completed' ? 'success' : outcomeMode === 'failed' ? 'error' : 'warning'}
                size="large"
                onClick={handleResolveOutcome}
                disabled={actionLoading || (outcomeMode !== 'completed' && !outcomeReason)}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
              >
                {actionLoading ? <CircularProgress size={24} /> : 'Confirm Outcome'}
              </Button>
              <Button
                fullWidth
                onClick={resetOutcomeForm}
                disabled={actionLoading}
                sx={{ fontWeight: 600, textTransform: 'none' }}
              >
                Back
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkerDashboard;
